// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

// Opt-in for automatic shot tracking (microphone impact detection + GPS marks).
//
// Deliberately a LOCAL, per-device setting rather than a profile flag: consent
// to run this device's microphone and GPS belongs to this device — following a
// profile to a new phone must never switch its sensors on silently. Default is
// off; nothing records until the player flips the switch and grants the
// runtime permissions.

import { create } from 'zustand';

const STORAGE_KEY = 'goffy:shotTracking';

type AsyncStorageLike = {
  getItem: (key: string) => Promise<string | null>;
  setItem: (key: string, value: string) => Promise<void>;
};

let asyncStorage: AsyncStorageLike | null = null;
try {
  asyncStorage = require('@react-native-async-storage/async-storage').default as AsyncStorageLike;
} catch {
  asyncStorage = null;
}

const getLocalStorage = () => (typeof window !== 'undefined' ? window.localStorage : null);

let storageOverride: AsyncStorageLike | null = null;

const readStored = async (): Promise<string | null> => {
  if (storageOverride) return storageOverride.getItem(STORAGE_KEY);
  const local = getLocalStorage();
  if (local) return local.getItem(STORAGE_KEY);
  if (asyncStorage) return asyncStorage.getItem(STORAGE_KEY);
  return null;
};

const writeStored = async (value: string): Promise<void> => {
  if (storageOverride) return storageOverride.setItem(STORAGE_KEY, value);
  const local = getLocalStorage();
  if (local) {
    local.setItem(STORAGE_KEY, value);
    return;
  }
  if (asyncStorage) await asyncStorage.setItem(STORAGE_KEY, value);
};

type ShotTrackingState = {
  enabled: boolean;
  /** True once storage has been read, or once the flag was set explicitly. */
  hydrated: boolean;
  setEnabled: (enabled: boolean) => void;
};

export const useShotTrackingState = create<ShotTrackingState>((set) => ({
  enabled: false,
  hydrated: false,
  setEnabled: (enabled) => {
    set({ enabled, hydrated: true });
    void writeStored(enabled ? 'on' : 'off').catch(() => {
      // Losing the mirror costs one re-toggle on the next cold start.
    });
  },
}));

export const hydrateShotTracking = async (): Promise<void> => {
  try {
    const stored = await readStored();
    if (stored === null) {
      useShotTrackingState.setState({ hydrated: true });
      return;
    }
    if (useShotTrackingState.getState().hydrated) return;
    useShotTrackingState.setState({ enabled: stored === 'on', hydrated: true });
  } catch {
    useShotTrackingState.setState({ hydrated: true });
  }
};

// Hydrates on import; before that the flag reads as off, which fails safe —
// tracking can only ever start late, never unasked.
void hydrateShotTracking();

export const useShotTrackingEnabled = (): boolean => useShotTrackingState((state) => state.enabled);

export const __setShotTrackingStorageForTests = (storage: AsyncStorageLike | null) => {
  storageOverride = storage;
};

export const __resetShotTrackingStateForTests = () => {
  useShotTrackingState.setState({ enabled: false, hydrated: false });
};
